/**
 * Subscription Types and Interfaces
 * Defines all subscription-related types for the Pyxi app
 */

export enum SubscriptionPlan {
  RESET_TABLE = 'reset_table',
  CULTURE_EDIT = 'culture_edit',
  ALL_ACCESS = 'all_access',
}

export enum SubscriptionType {
  MONTHLY = 'monthly',
  ONE_TIME_PASS = 'one_time_pass',
}

export interface SubscriptionFeature {
  id: string;
  name: string;
  description: string;
  included: boolean;
  icon?: string;
}

export interface SubscriptionPricing {
  monthly?: number;
  oneTimePass?: number;
  currency: string;
}

export interface SubscriptionPlanDetails {
  id: string;
  name: string;
  shortName?: string;
  description: string;
  pricing: SubscriptionPricing;
  features: SubscriptionFeature[];
  badge?: string;
  highlighted?: boolean;
  color: string;
  gradientStart?: string;
  gradientEnd?: string;
  plusOneGuest?: PlusOneGuestInfo; // +1 guest benefit info
}

export interface UserSubscription {
  planId: SubscriptionPlan;
  type: SubscriptionType;
  startDate: Date;
  endDate?: Date;
  isActive: boolean;
  autoRenew: boolean;
}

export interface SubscriptionCheckoutData {
  planId: SubscriptionPlan;
  type: SubscriptionType;
  amount: number;
  currency: string;
  planName: string;
}

/**
 * Active Subscription response from API
 */
export interface SubscriptionFeatureList {
  unlimited_dinners?: boolean;
  culture_edit_events?: boolean;
  monthly_pyxi_party?: number;
  priority_matching?: boolean;
  no_show_fee?: Record<string, string>;
  display_text: string;
  bring_plus_one_fee?: string | number; // '£2', 'Free', etc.
  last_minute_join_hours?: number;
  best_match_event?: {
    enabled: boolean;
    min_dinners_required?: number;
  };
  cancel_anytime?: boolean;
}

export interface SubscriptionPlanInfo {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  stripe_price_id: string;
  is_custom: boolean;
  is_active: boolean;
  feature_list: SubscriptionFeatureList;
  last_minute_join_hours: number | null;
}

export interface PlanQuotaByServiceType {
  CULTURAL_EDIT?: number;
  HOUSEWARMING?: number;
  THE_REST_TABLE?: number;
  [key: string]: number | undefined;
}

export interface ActiveSubscription {
  id: string;
  uid: string;
  plan_id: string;
  stripe_subscription_id: string;
  status: 'active' | 'inactive' | 'expired' | 'cancelled';
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
  plan: SubscriptionPlanInfo;
  can_book_event?: boolean | null;
  event_service_type?: string | null;
  plan_quota_remaining_by_service_type?: PlanQuotaByServiceType;
  unlimited_for_event?: boolean | null;
}

/**
 * Cancel Subscription API Response
 */
export interface CancelSubscriptionResponse {
  message: string;
  subscription: {
    stripe_subscription_id: string;
    uid: string;
    start_date: string;
    created_at: string;
    id: string;
    plan_id: string;
    status: 'canceled' | 'cancelled';
    end_date: string;
    updated_at: string;
  };
}

export interface AffectedOrder {
  order_id: string;
  payment_intent_id: string;
  uid: string;
  event_id: number;
  total_amount: number;
  total_tickets: number;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  subscription_booking: boolean;
  is_late: boolean;
  is_on_time: boolean;
  is_not_coming: boolean;
  guest_uids: string[];
  order_details: {
    order_id: string;
    ticket_id: string;
    count: number;
    name: string;
    currency: string;
    price: number;
    discounted_price: number;
    description: string;
    qr_codes: {
      id: string;
      order_detail_id: string;
      qr_code: string;
      is_checked_in: boolean;
      is_verified: boolean;
    }[];
  }[];
  event: {
    event_id: number;
    name: string;
    start_date: string;
    end_date: string;
    description: string;
    image_url: string;
    address_name: string;
    event_service_type: string;
  };
  promo_codes?: {
    promo_code_id: string;
    name: string;
    description: string;
    discount_percentage: number;
    minimum_amount: number;
    minimum_tickets: number;
    expires_at: string;
  };
  user: {
    uid: string;
    first_name: string;
    last_name: string;
    email: string;
    photo: string;
    gender: string;
    date_of_birth: string;
    postal_code: string;
    postal_area: string;
    device_location: string;
  };
}
